import { FC, useEffect, useLayoutEffect, useState } from 'react';
import { useQueryClient } from 'react-query';

import Helmet from '@/components/meta/Helmet';
import CurrentCampaignTemplate from '@/components/templates/CurrentCampaign';
import { Campaigns } from '@/components/templates/CurrentCampaign/types';
import { useSetBreadcrumbContext } from '@/contexts/breadcrumb';
import { useLayoutContext } from '@/contexts/layout';
import { useUserContext } from '@/contexts/user';
import { useGetEnteringCampaignIdsLazyQuery } from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { client } from '@/lib/microcms';
import { Campaign } from '@/lib/microcms/model';

const CurrentCampaign: FC = () => {
  const { setLayoutStyles } = useLayoutContext();
  const setBreadcrumbs = useSetBreadcrumbContext();

  useEffect(() => {
    setBreadcrumbs([
      {
        name: '実施中のキャンペーン',
        to: '/current_campaign',
      },
    ]);
  }, []);

  useLayoutEffect(() => {
    setLayoutStyles();
  }, []);

  const queryClient = useQueryClient();
  const { isMember } = useUserContext();

  const [campaigns, setCampaigns] = useState<Campaigns>({
    entries: [],
    others: [],
  });
  const [fetching, setFetching] = useState<boolean>(false);

  const [loadEnteringCampaignIds, { data }] =
    useGetEnteringCampaignIdsLazyQuery({
      fetchPolicy: 'network-only',
      errorPolicy: 'ignore',
    });

  const fetchContents = async () => {
    const nowISO = dayjst().toISOString();

    queryClient
      .fetchQuery(
        ['current_campaigns', dayjst().format('YYYY-MM-DD')],
        async () =>
          client.getContents('campaign', {
            fields:
              'id,featuredImage,title,entryCampaign,entryCampaignName,entryTime,endTime,publishedAt,updatedAt',
            limit: 100,
            filters: `nonPublic[equals]false[and]endTime[greater_than]${nowISO}[and]startTime[less_than]${nowISO}`,
            orders:
              import.meta.env.NODE_ENV === 'production'
                ? '-publishedAt'
                : undefined,
          }),
      )
      .then(res => {
        const entries: Campaign[] = [];
        const others: Campaign[] = [];

        res.forEach(c => {
          if (c.entryCampaign !== null || c.entryCampaignName) {
            entries.push(c);
            return;
          }
          others.push(c);
        });
        setCampaigns({ entries, others });
      })
      .finally(() => setFetching(false));
  };

  useEffect(() => {
    setFetching(true);
    fetchContents();

    if (isMember) {
      loadEnteringCampaignIds();
    }
  }, []);

  return (
    <>
      <Helmet
        meta={{
          title: '実施中のキャンペーン',
          description:
            'DMM競輪の実施中のキャンペーン情報ページ。お得なキャンペーン情報をご案内します。',
          robots: 'noindex',
        }}
      />
      <CurrentCampaignTemplate
        campaigns={campaigns}
        enteringCampaignIds={
          (data?.enteringCampaignIDs || []) as Campaign['id'][]
        }
        loading={fetching}
      />
    </>
  );
};
export default CurrentCampaign;
